class ProductLabels extends window.BAO.CustomElement() {
  static FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]'

  connectedCallback () {
    super.connectedCallback()

    if (this.els.options?.exists) {
      this.setupCarouselA11y()
    }
  }

  setupListeners () {
    this.handleMouseEnter = this.handleMouseEnter.bind(this)
    this.handleMouseLeave = this.handleMouseLeave.bind(this)

    this.els.item.elements.forEach((item) => {
      this.listeners.add(item, 'mouseenter', this.handleMouseEnter)
      this.listeners.add(item, 'mouseleave', this.handleMouseLeave)
    })

    if (!this.els.options?.exists) return

    this.listeners.add(this.els.options.element, 'scroll', () => {
      this.updateCarouselState()
    })
    this.listeners.add(window, 'resize', () => {
      this.updateCarouselState()
    })
    this.listeners.add(this.els.previous.element, 'click', () => {
      this.moveCarousel(-1)
    })
    this.listeners.add(this.els.next.element, 'click', () => {
      this.moveCarousel(1)
    })

    this.updateCarouselState()
  }

  setupCarouselA11y () {
    this.carouselObserver = new IntersectionObserver(
      () => this.updateCarouselState(),
      { root: this.els.options.element, threshold: 0.6 },
    )

    this.els.item.elements.forEach((item) => {
      this.carouselObserver.observe(item)
    })
  }

  updateCarouselState () {
    const options = this.els.options.element
    const containerRect = options.getBoundingClientRect()

    this.els.item.elements.forEach((item) => {
      const itemRect = item.getBoundingClientRect()
      const minimumVisibleWidth =
        Math.min(itemRect.width, containerRect.width) * 0.6
      const isVisible =
        itemRect.right > containerRect.left &&
        itemRect.left < containerRect.right &&
        Math.min(itemRect.right, containerRect.right) -
          Math.max(itemRect.left, containerRect.left) >=
          minimumVisibleWidth

      item.setAttribute('aria-hidden', String(!isVisible))
      this.setFocusableState(item, !isVisible)
    })

    this.els.previous.element.disabled = options.scrollLeft <= 1
    this.els.next.element.disabled =
      options.scrollLeft + options.clientWidth >= options.scrollWidth - 1
  }

  setFocusableState (item, isHidden) {
    const focusableElements = item.matches(ProductLabels.FOCUSABLE_SELECTOR)
      ? [item, ...item.querySelectorAll(ProductLabels.FOCUSABLE_SELECTOR)]
      : [...item.querySelectorAll(ProductLabels.FOCUSABLE_SELECTOR)]

    focusableElements.forEach((element) => {
      if (isHidden) {
        if (!element.hasAttribute('data-product-labels-original-tabindex')) {
          element.setAttribute(
            'data-product-labels-original-tabindex',
            element.getAttribute('tabindex') ?? '',
          )
        }

        element.setAttribute('tabindex', '-1')
        return
      }

      if (!element.hasAttribute('data-product-labels-original-tabindex')) {
        return
      }

      const originalTabindex = element.getAttribute(
        'data-product-labels-original-tabindex',
      )

      if (originalTabindex === '') {
        element.removeAttribute('tabindex')
      } else {
        element.setAttribute('tabindex', originalTabindex)
      }

      element.removeAttribute('data-product-labels-original-tabindex')
    })
  }

  moveCarousel (direction) {
    const options = this.els.options.element
    const containerRect = options.getBoundingClientRect()
    // Ignore sub-pixel/rounding overflow so an item that is only barely
    // clipped isn't treated as "the next item to scroll to" (which would
    // otherwise produce an imperceptible, near-zero scroll on click).
    const OVERFLOW_TOLERANCE = 2
    // For Previous, search from the end backward so we find the nearest
    // off-screen item before the viewport, rather than the very first
    // off-screen item in the whole list.
    const elements =
      direction > 0
        ? this.els.item.elements
        : [...this.els.item.elements].reverse()
    const item = elements.find((element) => {
      const rect = element.getBoundingClientRect()

      return direction > 0
        ? rect.right > containerRect.right + OVERFLOW_TOLERANCE
        : rect.left < containerRect.left - OVERFLOW_TOLERANCE
    })

    if (!item) return

    const itemRect = item.getBoundingClientRect()
    const delta =
      direction > 0
        ? itemRect.right - containerRect.right
        : itemRect.left - containerRect.left

    // Use an immediate scroll so button navigation also works in browsers and
    // embedded webviews that do not reliably run programmatic smooth scrolling.
    options.scrollTo({ left: options.scrollLeft + delta, behavior: 'auto' })
    this.updateCarouselState()
  }

  handleMouseEnter (e) {
    this.els.name.element.textContent = e.currentTarget.dataset.name
  }

  handleMouseLeave () {
    this.els.name.element.textContent = this.els.name.element.dataset.default
  }

  disconnectedCallback () {
    this.carouselObserver?.disconnect()
    super.disconnectedCallback()
  }
}

if (!customElements.get('product-labels')) {
  customElements.define('product-labels', ProductLabels)
}
